import { Loader2 } from 'lucide-react';
import Modal from './Modal';

interface ConfirmDeleteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isSubmitting: boolean;
  title?: string;
  entityLabel: string;
  entityName?: string;
  /** Replaces the default "This action cannot be undone." tail.
   *  Not every destructive-looking action is irreversible — a social-feed takedown
   *  is a soft-delete a moderator can reverse from the same queue — and telling an
   *  admin otherwise makes them hesitate on a safe action, or distrust the copy
   *  when the screen behind the dialog says the opposite. */
  consequenceNote?: string;
  /** Label for the confirm button. Defaults to "Delete". */
  confirmLabel?: string;
}

export default function ConfirmDeleteModal({
  isOpen,
  onClose,
  onConfirm,
  isSubmitting,
  title = 'Delete',
  entityLabel,
  entityName,
  consequenceNote = 'This action cannot be undone.',
  confirmLabel = 'Delete',
}: ConfirmDeleteModalProps) {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="sm"
      footer={
        <div className="flex w-full flex-col gap-3 sm:flex-row sm:justify-end">
          <button
            type="button"
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-red-700 disabled:opacity-50"
            onClick={onConfirm}
            disabled={isSubmitting}
          >
            {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
            {confirmLabel}
          </button>
        </div>
      }
    >
      <p className="text-sm text-gray-600">
        Are you sure you want to delete the {entityLabel}{' '}
        <span className="font-semibold text-gray-900">{entityName}</span>?{' '}
        {consequenceNote}
      </p>
    </Modal>
  );
}
